import { InputCheckbox } from '@creditornot/cb-checkbox';
import { cbSemanticCssVars, colors } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { usePluralization } from 'common/hooks';
import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';
import { VenueWithSharedMenuLinkInfo } from 'modules/shared-menu/hooks/useVenuesWithSelectionLinks';

type Props = {
  venues: VenueWithSharedMenuLinkInfo[];
  onDeselectVenue: (venueId: string) => void;
};

const Root = styled.div`
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${cbSemanticCssVars.surfaceMain};
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 12px 24px;
  width: 100%;
  background: ${cssThemedValue({ light: colors.pepper4, dark: colors.salt4 })};
`;

const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-end;
  margin-block-end: 12px;
`;

const StyledInputCheckbox = styled(InputCheckbox)`
  margin-inline-end: 12px;
`;

const VenueItem = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
  overflow: visible;
  white-space: nowrap;

  & + & {
    margin-block-start: 16px;
  }
`;

const MINIMISED_LIST_TRESHOLD = 2;

export const SelectedVenuesV2 = ({ venues, onDeselectVenue }: Props) => {
  const intl = useIntl();
  const { FormattedPluralMessage } = usePluralization();

  return (
    <Root>
      <Container>
        <HeaderContainer>
          <T.Small>
            <FormattedMessage id="shared-menu.select-venues.selected" />
          </T.Small>
        </HeaderContainer>
        {venues.length === 1 && (
          <VenueItem>
            <StyledInputCheckbox onChange={() => onDeselectVenue(venues[0].id)} checked />
            <T.Body3>{`${localize(venues[0].name, intl.locale)} `}</T.Body3>
          </VenueItem>
        )}
        {venues.length === 2 && (
          <VenueItem>
            <StyledInputCheckbox readOnly disabled checked />
            <T.Body3> {`${localize(venues[0].name, intl.locale)}, ${localize(venues[1].name, intl.locale)} `}</T.Body3>
          </VenueItem>
        )}
        {venues.length > 2 && (
          <VenueItem>
            <StyledInputCheckbox readOnly disabled checked />
            <T.Body3>
              {`${localize(venues[0].name, intl.locale)}, ${localize(venues[1].name, intl.locale)} `}
              <b>
                <FormattedPluralMessage
                  id="shared-menu.select-venues.venue-list-minimised"
                  count={venues.length - MINIMISED_LIST_TRESHOLD}
                />
              </b>
            </T.Body3>
          </VenueItem>
        )}
      </Container>
    </Root>
  );
};
